'use client';

/**
 * HomepageV3 — "How to Start Investing" steps section.
 *
 * Ported 1:1 from `design-refs/source/private-home.css` (`.hiw*` classes) and
 * `design-refs/source/private-home.js` (`buildHowItWorks` — STEPS + safeguard copy).
 *
 * Layout: a two-column grid — left intro (serif title, lead, CTA, "We Safeguard
 * Your Investment" card) and a dark numbered-steps panel on the right. Collapses
 * to a single column on mobile.
 */
import * as React from 'react';
import * as Sentry from 'utils/errorTracking';
import { styled } from '@mui/material/styles';
import { trackEventWithGTM } from 'utils/analytics';

import { FONTS } from '../theme/tokens';

export interface HowToStep {
  /** Step title (rendered uppercase) */
  title: string;
  /** Step description */
  desc: string;
}

export interface HowToStartSectionProps {
  /** Override the step list (defaults to the source copy). */
  steps?: HowToStep[];
  /** Lead paragraph variant — pre-IPO (logged out) or early-stage (open). */
  variant?: 'pre-IPO' | 'early-stage';
  /** Override the serif section title. */
  title?: string;
  /** Override the lead paragraph (otherwise composed from `variant`). */
  lead?: string;
  /** Override the CTA label. */
  ctaLabel?: string;
  /** Analytics page key, so non-homepage surfaces report their own page. */
  trackingPage?: string;
  /** "Get Started" CTA click handler. Called after analytics tracking fires. */
  onCtaClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const DEFAULT_STEPS: HowToStep[] = [
  {
    title: 'Explore',
    desc: 'Browse startup and pre-IPO offerings across AI, defense, quantum computing, and more. Filter by category, minimum investment, or availability.',
  },
  {
    title: 'Join & Verify',
    desc: 'Create a free account and set up your investor profile. Pre-IPO offerings are available to accredited investors only — verification is straightforward and takes just a few minutes.',
  },
  {
    title: 'Invest',
    desc: 'Review the full offering details, disclosures, and terms. Minimums typically start at $500 for startup investments and $15,000 for pre-IPO offerings. You can also connect with one of our registered reps to discuss any deal before you commit.',
  },
  {
    title: 'Track Performance',
    desc: 'Track your holdings in your StartEngine dashboard. If a liquidity event occurs — an IPO, acquisition, or private sale — our team walks you through your options.',
  },
];

const Section = styled('section')({
  background: 'var(--cream)',
  borderTop: '1px solid var(--edge)',
});

const Inner = styled('div')({
  maxWidth: 1240,
  margin: '0 auto',
  padding: '84px var(--pad)',
  display: 'grid',
  gridTemplateColumns: '0.92fr 1.08fr',
  gap: 72,
  alignItems: 'start',
  // PD-35996 — stack at the source `.hiw-inner` tablet breakpoint (≤1080px),
  // not 980px, so tablet widths (834/1024) match the handoff single-column flow.
  '@media (max-width: 1080px)': {
    gridTemplateColumns: '1fr',
    gap: 44,
  },
  '@media (max-width: 768px)': {
    padding: '54px var(--pad)',
  },
});

const Title = styled('h2')({
  fontFamily: FONTS.serif,
  fontSize: 46,
  fontWeight: 400,
  letterSpacing: '-0.015em',
  lineHeight: 1.05,
  color: 'var(--ink)',
  margin: 0,
  '@media (max-width: 768px)': {
    fontSize: 34,
  },
});

const Lead = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: 18.5,
  lineHeight: 1.72,
  color: 'var(--ink-2)',
  maxWidth: 440,
  marginTop: 22,
  marginBottom: 0,
});

const StartButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  marginTop: 30,
  fontFamily: FONTS.sans,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--teal)',
  border: 'none',
  padding: '15px 28px',
  borderRadius: 14,
  cursor: 'pointer',
  transition: 'background 0.15s, transform 0.15s',
  '&:hover': {
    background: '#2BE3CE',
    transform: 'translateY(-1px)',
  },
});

const Steps = styled('div')({
  background: 'radial-gradient(130% 130% at 86% 0%, #0E3236 0%, #07191A 70%)',
  borderRadius: 24,
  padding: '12px 44px',
  '@media (max-width: 768px)': {
    padding: '6px 24px',
  },
});

const Step = styled('div')({
  display: 'grid',
  gridTemplateColumns: '52px 1fr',
  gap: 24,
  alignItems: 'start',
  padding: '28px 0',
  borderBottom: '1px solid rgba(233,215,182,0.12)',
  '&:last-of-type': {
    borderBottom: 'none',
  },
  '@media (max-width: 768px)': {
    gridTemplateColumns: '40px 1fr',
    gap: 16,
    padding: '22px 0',
  },
});

const StepNum = styled('div')({
  fontFamily: FONTS.serif,
  fontSize: 30,
  fontWeight: 400,
  color: 'var(--gold)',
  lineHeight: 1,
  '@media (max-width: 768px)': {
    fontSize: 26,
  },
});

const StepTitle = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#fff',
  marginBottom: 9,
});

const StepDesc = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: 16,
  lineHeight: 1.65,
  color: 'var(--c-text-2)',
  maxWidth: 470,
});

export function HowToStartSection({
  steps = DEFAULT_STEPS,
  variant = 'pre-IPO',
  title = 'How to Start Investing',
  lead,
  ctaLabel = 'Get Started →',
  trackingPage = 'homepage_v3',
  onCtaClick,
}: HowToStartSectionProps): React.ReactElement {
  const leadCopy =
    lead ??
    `Tapping into ${variant} opportunities is easier than you think. We've built the infrastructure, so all you have to do is find the companies you believe in. Browse, verify, and invest in minutes.`;

  const handleCtaClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      trackEventWithGTM('clicked_get_started_button', {
        page: trackingPage,
        surface: 'how_to_start',
        variant,
      });
    } catch (e) {
      Sentry.captureException(e, { tags: { area: trackingPage } });
    }
    onCtaClick?.(event);
  };

  return (
    <Section className="hiw" data-cy="hiw-section" aria-labelledby="hiw-title">
      <Inner className="hiw-inner">
        <div className="hiw-left" data-cy="hiw-left">
          <Title id="hiw-title" className="hiw-title" data-cy="hiw-title">
            {title}
          </Title>
          <Lead className="hiw-lead" data-cy="hiw-lead">
            {leadCopy}
          </Lead>
          <StartButton type="button" className="hiw-btn" onClick={handleCtaClick} data-cy="hiw-cta">
            {ctaLabel}
          </StartButton>
        </div>

        <Steps className="hiw-steps" data-cy="hiw-steps">
          {steps.map((step, i) => (
            <Step key={step.title} className="hiw-step" data-cy="hiw-step">
              <StepNum className="hiw-num" data-cy="hiw-num">
                {`0${i + 1}`}
              </StepNum>
              <div className="hiw-step-body">
                <StepTitle className="hiw-step-title">{step.title}</StepTitle>
                <StepDesc className="hiw-step-desc">{step.desc}</StepDesc>
              </div>
            </Step>
          ))}
        </Steps>
      </Inner>
    </Section>
  );
}

export default HowToStartSection;
